import React, { Component } from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import Select from "react-select";
import { compose } from "redux";
import classNames from "classnames";
import { bool, string, number, func, arrayOf, shape, oneOfType } from "prop-types";

import { selectors as i18nSelectors } from "reducers/i18n";
import { getInteger } from "util/config";
import { countDecimalPlaces, numberFormat } from "util/number";

import FieldError from "pages/_components/fields/FieldError";
import FieldLabel from "pages/_components/fields/FieldLabel";
import withFocus from "pages/_components/withFocus";
import FieldHint from "./FieldHint";

export class AmountField extends Component {
    static propTypes = {
        clearable: bool,
        disableSelect: bool,
        disableNumber: bool,
        disable: bool,
        hideLabel: bool,
        hideCurrency: bool,
        labelText: string,
        hintLabel: string,
        idForm: string,
        name: string.isRequired,
        optional: string,
        isFocused: bool.isRequired,
        hasError: bool,
        lang: string.isRequired,
        maximumDecimals: number.isRequired,
        minimumDecimals: number.isRequired,
        toggleIsFocused: func.isRequired,
        onCurrencyChange: func.isRequired,
        onInputChange: func.isRequired,
        onBlur: func.isRequired,
        size: string,
        error: string,
        currency: string,
        amount: oneOfType([string, number]),
        maxLength: string,
        data: shape({
            options: arrayOf(
                shape({
                    id: number.isRequired,
                    label: string.isRequired,
                }),
            ),
        }),
        field: shape({}),
        allowNegative: bool,
        fixedDecimalScale: bool,
        integerLength: number,
        decimalLength: number,
        withValueCap: bool,
    };

    static defaultProps = {
        clearable: true,
        disableSelect: false,
        disableNumber: false,
        disable: false,
        hideLabel: false,
        hideCurrency: false,
        labelText: null,
        hintLabel: null,
        idForm: "",
        size: "",
        error: "",
        optional: "",
        hasError: false,
        amount: "",
        currency: "",
        maxLength: "20",
        data: { options: [] },
        field: {},
        allowNegative: false,
        fixedDecimalScale: false,
        withValueCap: false,
        integerLength: 20,
        decimalLength: 2,
    };

    renderLabel = () => {
        const { idForm, name, labelText, optional } = this.props;
        const id = `${idForm}.${name}`;
        if (labelText !== null) {
            return <FieldLabel labelText={labelText} optional={optional} idField={id} />;
        }
        return <FieldLabel labelKey={`${idForm}.${name}.label`} optional={optional} idField={id} />;
    };

    render() {
        /* eslint-disable react/prop-types */
        const {
            field,
            idForm,
            clearable,
            data,
            disableSelect,
            disableNumber,
            disable,
            hideLabel,
            hintLabel,
            isFocused,
            lang,
            maximumDecimals,
            minimumDecimals,
            toggleIsFocused,
            hideCurrency,
            size,
            hasError,
            onCurrencyChange,
            onInputChange,
            currency,
            amount,
            maxLength,
            onBlur,
            error,
            allowNegative,
            fixedDecimalScale,
            integerLength,
            decimalLength,
            withValueCap,
            placeholder,
            prefix,
        } = this.props;
        /* eslint-enable react/prop-types */

        let options;
        if (!hideCurrency) {
            ({ options } = data);
        }
        const { decimalSeparator, thousandSeparator } = numberFormat(lang);
        const decimalPlaces = this.amountRef ? countDecimalPlaces(this.amountRef.value, decimalSeparator) : 0;
        const decimalScale = Math.max(Math.min(decimalPlaces, maximumDecimals), minimumDecimals);
        const idField = `${idForm}.${field.name}`;

        const _withValueCap = (inputObj) => {
            if (!withValueCap) {
                return true;
            }

            const maxValue = Number(`${Array(integerLength + 1).join("9")}.${Array(decimalLength + 1).join("9")}`);
            const { value } = inputObj;
            if (value <= maxValue) {
                return true;
            }
            return false;
        };

        return (
            <div
                className={classNames("form-group", "form-group--composite", {
                    "has-error": hasError,
                    "has-focus": isFocused,
                    "form-group--small": size === "small",
                })}
                onFocus={toggleIsFocused}>
                {!hideLabel && this.renderLabel()}
                <div className={`input-group-standard${disable ? " is-disabled" : ""}`}>
                    {!hideCurrency && options.length === 1 ? (
                        <span className="currency">{options[0].label}</span>
                    ) : (
                        !hideCurrency && (
                            <Select
                                clearable={clearable}
                                className="currency-selector slideFromBottom flex-container"
                                disabled={disableSelect || disable}
                                onChange={onCurrencyChange}
                                options={options.map(({ id, label }) => ({ value: id, label }))}
                                searchable={false}
                                value={currency}
                                onBlur={() => {
                                    toggleIsFocused();
                                    onBlur();
                                }}
                                optionClassName="needsclick"
                            />
                        )
                    )}
                    <NumberFormat
                        name={field.name}
                        id={idField}
                        allowNegative={allowNegative}
                        className="form-control"
                        decimalScale={decimalScale}
                        decimalSeparator={decimalSeparator}
                        maxLength={maxLength}
                        onChange={onInputChange}
                        thousandSeparator={thousandSeparator}
                        type="text"
                        onBlur={() => {
                            toggleIsFocused();
                            onBlur();
                        }}
                        value={amount}
                        disabled={disableNumber || disable}
                        fixedDecimalScale={fixedDecimalScale}
                        isAllowed={_withValueCap}
                        placeholder={placeholder}
                        prefix={prefix}
                    />
                </div>
                {hasError && <FieldError error={error} />}
                {hintLabel && <FieldHint text={hintLabel} />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    lang: i18nSelectors.getLang(state),
    maximumDecimals: getInteger("defaultDecimal.maximum"),
    minimumDecimals: getInteger("defaultDecimal.minimum"),
});

export default compose(connect(mapStateToProps), withFocus)(AmountField);
